import React, { useEffect, useState } from 'react';
import ModalPortal from '../../ModalPortal';
import { Icon, Icons } from '../../Icon';
import Button from '../../Form/Button';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import TextField from '../../Form/TextField';
import { useCreateUserAccount } from '../../../hooks/queries-and-mutations/auth';
import { CreateAccountDto } from '../../../types/user.type';
import { toast } from 'react-toastify';

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
  onSignInClick: () => void;
};

export default function AddAccountModal({ onCancel, onSuccess, onSignInClick }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useCreateUserAccount();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First Name is required').nullable(),
      lastName: Yup.string().required('Last Name is required').nullable(),
      email: Yup.string().email('Invalid Email').required('Email is required').nullable(),
      password: Yup.string()
        .required('Password is required')
        .nullable()
        .matches(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,40}$/,
          'Password must have at least one uppercase, one lowercase, one number, and be at least 8 characters long'
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), ''], 'Passwords must match')
        .required('Confirm password is required')
        .nullable(),
    }),
    onSubmit: (values) => {
      setIsSubmitting(true);
      onSubmit(values);
    },
  });

  const onSubmit = (createAccountDto: CreateAccountDto) => {
    const payLoad = {
      email: createAccountDto.email.toLocaleLowerCase(),
      password: createAccountDto.password,
      firstName: createAccountDto.firstName,
      lastName: createAccountDto.lastName,
    };
    mutate(payLoad);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast.success(
        'Check your mail a confirmation mail has been sent to verify and activate your User Account.'
      );
      setIsSubmitting(false);
      formik.resetForm();
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess]);

  return (
    <div>
      <ModalPortal>
        <div className="fixed top-0 z-50 flex h-screen w-screen items-center justify-center bg-[#000000] bg-opacity-60">
          <div className="flex flex-col items-end justify-end px-32">
            <div
              className="ml-6 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={onCancel}
            >
              <Icon name={Icons.Close} />
            </div>
            <div className="rounded-[20px] min-w-[400px] max-[500px]:min-w-[320px] bg-white py-8 min-[400px]:px-8 max-[400px]: px-4 font-circular text-2xl font-normal text-[#353535]">
              <div className="flex flex-col">
                <div className="py-5 text-left max-w-xss">
                  <p className="text-xl font-medium text-black font-inter">Add Account</p>
                  <p className="text-sm font-normal text-black font-inter">
                    Create a separate account with another email address
                  </p>
                </div>

                <div className="mx-auto w-full max-w-[440px]">
                  <form className="space-y-0.5" onSubmit={formik.handleSubmit}>
                    <TextField
                      name="firstName"
                      type="text"
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleChange}
                      label="First Name"
                      error={formik.touched.firstName ? formik.errors.firstName : ''}
                    />
                    <TextField
                      name="lastName"
                      type="text"
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleChange}
                      label="Last Name"
                      error={formik.touched.lastName ? formik.errors.lastName : ''}
                    />
                    <TextField
                      name="email"
                      type="text"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleChange}
                      label="Email"
                      error={formik.touched.email ? formik.errors.email : ''}
                    />
                    <TextField
                      name="password"
                      type="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleChange}
                      label="New Password"
                      showPasswordToggler
                      error={formik.touched.password ? formik.errors.password : ''}
                    />
                    <TextField
                      name="confirmPassword"
                      type="password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleChange}
                      label="Confirm password"
                      showPasswordToggler
                      error={
                        formik.touched.confirmPassword
                          ? formik.errors.confirmPassword
                          : ''
                      }
                    />
                    <div>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        label="Create Account"
                        className="mt-5"
                        loading={formik.isSubmitting || isLoading}
                        disabled={formik.isSubmitting || isSubmitting}
                      />
                    </div>
                  </form>
                  <div className="text-center text-[#00000066] text-xs mt-4 flex items-center justify-center">
                    Already have an account?
                    <div
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer text-[#061A40] ml-1"
                      onClick={onSignInClick}
                    >
                      Login
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>
    </div>
  );
}
